/**
 * Générateur — Additionner plusieurs nombres décimaux relatifs.
 * Attendu ID : 917 | Niveau : 5ème | Domaine : Nombres relatifs
 *
 * Libellé officiel (spécification 917.json) : « Additionner plusieurs nombres
 * décimaux relatifs. »
 *
 * Plan de cartes (version conforme à la spécification, campagne 5e 2026-09-22) :
 *
 *   Carte « groupes » — Regrouper les positifs et les négatifs (niveaux 1 à 5)
 *     texte_trou_saisie : la correction en TROIS LIGNES DISTINCTES — somme des
 *     positifs, somme des négatifs, résultat — jamais une chaîne d'égalités ;
 *     qcm_unique : distracteurs = les deux sommes de groupes ajoutées au lieu
 *     d'être soustraites, un terme compté deux fois, le signe inversé ;
 *     tableau_vf : cinq gabarits à prédicat variable.
 *
 *   Carte « astuce » — Regrouper astucieusement (niveaux 2 à 4)
 *     qcm_unique : la paire de termes opposés, UNIQUE par construction ;
 *     texte_trou_saisie : l'opposé repéré, puis la somme totale.
 *
 *   Carte « situation » — Le bilan d'une situation (niveau 6)
 *     Contexte tiré dans la liste fermée de la spec (gains et pertes,
 *     températures, altitudes, mouvements d'un compte). Le nombre d'événements
 *     du SECOND type n'est jamais donné : il se déduit du total.
 *     qcm_unique : le sens du bilan ET son montant, en une proposition ;
 *     texte (saisie) : le montant du bilan, comparé comme nombre.
 *
 * Spécification (917.json) :
 *   Bornes appliquées : nombre_de_termes 3..6 ;
 *     nombre_de_decimales_de_chaque_terme 0..2 (`origine: prescrit`), appliqué
 *     à CHAQUE terme, les termes d'une même somme pouvant différer ;
 *     termes_de_la_somme -20..20, aucun terme nul ;
 *     repartition_des_signes : au moins un terme de chaque signe, jamais plus
 *     de trois termes consécutifs de même signe, et trois d'affilée une fois
 *     sur cinq au plus ;
 *     forme_d_ecriture_de_l_enonce : les trois écritures de la liste fermée ;
 *     contexte_de_la_situation : les quatre contextes de la liste fermée ;
 *     montants_de_la_situation 0,5..20 (intersection avec termes_de_la_somme,
 *     voir « Non tranché »).
 *   Exclusions : aucun terme nul ; aucune somme de termes tous de même signe ;
 *     aucune imbrication de parenthèses ; en situation, aucun bilan nul et
 *     gain unitaire ≠ perte unitaire ; aucune proposition de QCM en double
 *     (le tirage est rejoué).
 *   Erreurs visées → mots-clés (_meta.params.distracteurs[].code,
 *   _meta.params.lignes[].code, erreurVisee) :
 *     « Enchaîner les additions deux à deux de gauche à droite et perdre un
 *       signe » → enchainement_gauche_droite (groupes_vf)
 *     « Additionner les deux sommes de groupes au lieu de les soustraire »
 *         → groupes_additionnes_au_lieu_de_soustraits (groupes_qcm_unique,
 *           groupes_vf, groupes_texte_trou_saisie)
 *     « Compter deux fois un terme lors du regroupement, ou en oublier un »
 *         → terme_compte_deux_fois (groupes_qcm_unique, groupes_vf, astuce)
 *     « Donner au résultat le signe du terme de plus grande distance à zéro »
 *         → signe_du_plus_grand_terme (groupes_qcm_unique, groupes_vf,
 *           situation_qcm_unique)
 *     « Aligner mal les rangs décimaux quand les termes n'ont pas le même
 *       nombre de décimales » → rangs_decimaux_mal_alignes (groupes_vf,
 *       groupes_texte_trou_saisie quand les décimales diffèrent)
 *     « Confondre le nombre d'événements et le montant de chacun »
 *         → effectifs_confondus_avec_montants (situation ×2)
 *     « Oublier de déduire le nombre d'événements qui n'est pas donné »
 *         → effectif_non_deduit (situation ×2)
 *   Graduation → cartes :
 *     niveau 1 (trois entiers, signes mêlés) et niveau 2 (quatre à cinq
 *       entiers) → carte groupes, tirages entiers ; niveau 3 (une décimale) et
 *       niveau 4 (deux décimales) → carte groupes et carte astuce ;
 *     niveau 5 (six termes, écriture simplifiée sans parenthèses) → carte
 *       groupes, forme imposée ; niveau 6 (situation avec effectif à déduire)
 *       → carte situation.
 *   Non tranché : montants_de_la_situation monte à 30 alors que
 *     termes_de_la_somme s'arrête à 20 ; amplitude_maximale_reglable n'a pas de
 *     mécanisme de réglage ; nombre_de_questions_de_la_serie n'a pas
 *     d'équivalent (un tirage = une question) ; les écritures « oui / non /
 *     vrai / faux » de la réponse fermée ne sont pas toutes acceptables dans
 *     un trou.
 *
 * Arithmétique en CENTIÈMES entiers. Signe « - » = tiret clavier partout.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 917;

const L_GROUPES = 'Regrouper les positifs et les négatifs';
const L_ASTUCE = 'Regrouper astucieusement';
const L_SITUATION = 'Le bilan d\'une situation';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Centièmes entiers → écriture française. cent(0) = « 0 ». */
const cent = (c) => {
    const s = c < 0 ? '-' : '';
    const a = Math.abs(c);
    const ent = Math.floor(a / 100);
    const r = a % 100;
    if (r === 0) return `${s}${ent}`;
    if (r % 10 === 0) return `${s}${ent},${r / 10}`;
    return `${s}${ent},${String(r).padStart(2, '0')}`;
};
const signe = (c) => (c === 0 ? '0' : c < 0 ? cent(c) : `+${cent(c)}`);

const CONSIGNE_RES = 'Écris chaque réponse en écriture décimale ; un résultat positif peut s\'écrire avec ou sans le signe +.';

// ═════════ Tirage des termes ═════════

/** Un terme non nul, |v| ≤ 20, avec exactement `dec` décimales. */
const tirerTerme = (dec) => {
    if (dec === 0) return randInt(1, 20) * 100;
    if (dec === 1) {
        let k = randInt(1, 199);
        if (k % 10 === 0) k += 1;
        return k * 10;
    }
    let k = randInt(1, 1999);
    if (k % 10 === 0) k += 1;
    return k;
};

/** Décimales possibles par niveau de la graduation. */
const NIVEAUX = {
    1: { n: [3, 3], dec: [0] },
    2: { n: [4, 5], dec: [0] },
    3: { n: [4, 5], dec: [1, 0] },
    4: { n: [4, 5], dec: [2, 1] },
    5: { n: [6, 6], dec: [2, 1, 0] },
};
const NIVEAUX_TIRES = [1, 2, 2, 3, 3, 4, 4, 5];

/** La plus longue suite de termes de même signe. */
const plusLongueSuite = (termes) => {
    let max = 1;
    let courant = 1;
    for (let i = 1; i < termes.length; i++) {
        courant = (termes[i] > 0) === (termes[i - 1] > 0) ? courant + 1 : 1;
        if (courant > max) max = courant;
    }
    return max;
};

/**
 * Range les termes : au moins un de chaque signe (garanti par le tirage),
 * jamais plus de trois consécutifs de même signe, et trois d'affilée une fois
 * sur cinq au plus (repartition_des_signes).
 */
const arranger = (termes) => {
    const limite = randInt(1, 5) === 1 ? 3 : 2;
    for (let essai = 0; essai < 60; essai++) {
        const t = shuffle(termes);
        if (plusLongueSuite(t) <= limite) return t;
    }
    // Repli déterministe : on alterne les signes tant qu'il reste des deux.
    const pos = termes.filter(v => v > 0);
    const neg = termes.filter(v => v < 0);
    const out = [];
    while (pos.length || neg.length) {
        if (pos.length) out.push(pos.shift());
        if (neg.length) out.push(neg.shift());
    }
    return out;
};

/** Les termes d'une somme, selon le niveau de la graduation. */
const tirerTermes = (niveau) => {
    const cfg = NIVEAUX[niveau];
    const n = randInt(cfg.n[0], cfg.n[1]);
    const nPos = n === 3 ? randInt(1, 2) : randInt(2, n - 2);
    const termes = [];
    for (let i = 0; i < n; i++) {
        termes.push((i < nPos ? 1 : -1) * tirerTerme(randomChoice(cfg.dec)));
    }
    return arranger(termes);
};

// ═════════ Écriture de l'énoncé (liste fermée de la spec) ═════════

const FORMES = ['tous', 'negatifs', 'simplifiee'];

const ecrireSomme = (termes, forme) => {
    if (forme === 'tous') return termes.map(v => `(${signe(v)})`).join(' + ');
    if (forme === 'negatifs') return termes.map(v => (v < 0 ? `(${cent(v)})` : cent(v))).join(' + ');
    // Écriture simplifiée : aucune parenthèse, le signe devient l'opération.
    return termes.map((v, i) => (i === 0
        ? cent(v)
        : `${v < 0 ? '-' : '+'} ${cent(Math.abs(v))}`)).join(' ');
};

/** Décalage des rangs décimaux : 2,5 devient 2,05 (erreur visée 5). */
const malAligne = (c) => {
    const s = c < 0 ? -1 : 1;
    const a = Math.abs(c);
    return s * (Math.floor(a / 100) * 100 + Math.floor((a % 100) / 10));
};

/** Tire nb vérités avec au moins une vraie et une fausse. */
const tirerVerites = (nb) => {
    const v = [];
    for (let i = 0; i < nb; i++) v.push(randomChoice([true, false]));
    if (v.every(x => x)) v[randInt(0, nb - 1)] = false;
    else if (v.every(x => !x)) v[randInt(0, nb - 1)] = true;
    return v;
};

const sommeDe = (t) => t.reduce((s, v) => s + v, 0);

// ═════════ Carte « groupes » ═════════

const LETTRES_SOMME = ['A', 'B', 'C', 'D', 'S', 'T'];

const genGroupesSaisie = () => {
    const niveau = randomChoice(NIVEAUX_TIRES);
    const termes = tirerTermes(niveau);
    const forme = niveau === 5 ? 'simplifiee' : randomChoice(FORMES);
    const lettre = randomChoice(LETTRES_SOMME);
    const P = sommeDe(termes.filter(v => v > 0));
    const N = sommeDe(termes.filter(v => v < 0));
    // Une étape par ligne : la correction expose le regroupement par signe en
    // lignes DISTINCTES, jamais en chaîne d'égalités (points_de_vigilance).
    const phrase = `${lettre} = ${ecrireSomme(termes, forme)}\n`
        + `Somme des termes positifs : {{${cent(P)}}}\n`
        + `Somme des termes négatifs : {{${cent(N)}}}\n`
        + `Résultat : {{${cent(P + N)}}}`;
    const payload = makeTexteTrou(phrase,
        `<p><b>Calcule ${lettre} en regroupant les termes positifs ensemble et les termes négatifs ensemble.</b> ${CONSIGNE_RES}</p>`,
        { ...opts(L_GROUPES), mode: 'saisie' });
    const decimalesMelees = new Set(termes.map(v => (v % 100 === 0 ? 0 : (v % 10 === 0 ? 1 : 2)))).size > 1;
    payload._meta.params = {
        termes, forme, niveau, P, N,
        erreurVisee: decimalesMelees ? 'rangs_decimaux_mal_alignes' : 'groupes_additionnes_au_lieu_de_soustraits',
    };
    return payload;
};

const genGroupesQcm = () => {
    const niveau = randomChoice(NIVEAUX_TIRES);
    const construire = () => {
        const termes = tirerTermes(niveau);
        const P = sommeDe(termes.filter(v => v > 0));
        const N = sommeDe(termes.filter(v => v < 0));
        const total = P + N;
        const double = randomChoice(termes);
        return {
            termes,
            total,
            propositions: [total, P - N, total + double, -total],
        };
    };
    let t = construire();
    for (let essai = 0; essai < 60 && new Set(t.propositions).size < 4; essai++) t = construire();
    if (new Set(t.propositions).size < 4) { // repli déterministe, vérifié à la main
        t = { termes: [500, -300, 200, -900], total: -500, propositions: [-500, 1900, -1400, 500] };
    }
    const forme = randomChoice(FORMES);
    const lettre = randomChoice(LETTRES_SOMME);
    const codes = ['groupes_additionnes_au_lieu_de_soustraits', 'terme_compte_deux_fois', 'signe_du_plus_grand_terme'];
    const distracteurs = t.propositions.slice(1).map((v, i) => ({ valeur: cent(v), code: codes[i] }));
    const payload = makeQcm(t.propositions.map(cent), cent(t.total),
        `<p><b>Que vaut ${lettre} = ${ecrireSomme(t.termes, forme)} ?</b></p>`,
        opts(L_GROUPES));
    payload._meta.params = { termes: t.termes, forme, niveau, distracteurs };
    return payload;
};

/**
 * Gabarits à prédicat variable. ctx : { termes, total, P, N, ecart, x, y, z }.
 */
const GABARITS_GROUPES = [
    {
        id: 'ordre', code: 'enchainement_gauche_droite',
        ligne: v => (v
            ? 'Dans une somme de plusieurs nombres relatifs, on peut regrouper les termes dans l\'ordre que l\'on veut.'
            : 'Dans une somme de plusieurs nombres relatifs, on doit calculer de gauche à droite, deux termes à la fois.'),
    },
    {
        id: 'regroupement', code: 'groupes_additionnes_au_lieu_de_soustraits',
        ligne: (v, c) => `Dans ${c.expr}, la somme des termes positifs vaut ${cent(v ? c.P : -c.N)}.`,
    },
    {
        id: 'resultat', code: 'signe_du_plus_grand_terme',
        ligne: (v, c) => `${c.expr} = ${cent(v ? c.total : -c.total)}.`,
    },
    {
        id: 'oubli', code: 'terme_compte_deux_fois',
        ligne: (v, c) => `Dans ${c.expr}, la somme des termes négatifs vaut ${cent(v ? c.N : c.N - c.termes[0])}.`,
    },
    {
        id: 'alignement', code: 'rangs_decimaux_mal_alignes',
        ligne: (v, c) => `${cent(c.x)} + ${cent(c.y)} + (${cent(c.z)}) = `
            + `${cent(v ? c.x + c.y + c.z : malAligne(c.x) + c.y + c.z)}.`,
    },
];

const genGroupesVf = () => {
    const niveau = randomChoice(NIVEAUX_TIRES);
    let termes = tirerTermes(niveau);
    // Le gabarit « résultat » exige un total non nul, et le gabarit « oubli »
    // un premier terme négatif non nul.
    for (let essai = 0; essai < 40 && (sommeDe(termes) === 0 || termes[0] === 0); essai++) {
        termes = tirerTermes(niveau);
    }
    if (sommeDe(termes) === 0) termes = [500, -300, 200, -900]; // repli déterministe
    const forme = randomChoice(FORMES);
    const ctx = {
        termes,
        expr: ecrireSomme(termes, forme),
        total: sommeDe(termes),
        P: sommeDe(termes.filter(v => v > 0)),
        N: sommeDe(termes.filter(v => v < 0)),
        x: tirerTerme(1),
        y: tirerTerme(2),
        z: -tirerTerme(0),
    };
    const gabarits = shuffle(GABARITS_GROUPES).slice(0, 4);
    const verites = tirerVerites(4);
    const lignes = gabarits.map((g, i) => ({
        template: g.id, code: g.code, vrai: verites[i], texte: g.ligne(verites[i], ctx),
    }));
    const payload = makeVraiFaux(
        lignes.map(l => l.texte),
        lignes.map(l => (l.vrai ? 0 : 1)),
        '<p><b>Pour chaque affirmation, coche Vrai ou Faux.</b></p>',
        opts(L_GROUPES));
    payload._meta.params = { termes, forme, niveau, lignes };
    return payload;
};

// ═════════ Carte « astuce » — Regrouper astucieusement ═════════

/** 5 termes dont UNE SEULE paire d'opposés ; valeurs absolues toutes distinctes. */
const tirerAstuce = () => {
    const dec = randomChoice([0, 1, 2]);
    const x = tirerTerme(dec);
    const absolues = new Set([x]);
    const autres = [];
    for (let essai = 0; essai < 80 && autres.length < 3; essai++) {
        const v = tirerTerme(randomChoice([0, 1, 2]));
        if (!absolues.has(v)) { absolues.add(v); autres.push(randomChoice([1, -1]) * v); }
    }
    [130, -270, 460, -815].forEach((v) => { // repli déterministe, dans les bornes
        if (autres.length < 3 && !absolues.has(Math.abs(v))) { absolues.add(Math.abs(v)); autres.push(v); }
    });
    // Au moins un terme de chaque signe parmi les cinq : la paire d'opposés en
    // fournit déjà un de chaque.
    return { x, termes: arranger([x, -x, ...autres]), reste: sommeDe(autres) };
};

const genAstuceQcm = () => {
    const { x, termes } = tirerAstuce();
    const [p1, p2] = termes.filter(v => Math.abs(v) === Math.abs(x));
    const correct = `${cent(p1)} et ${cent(p2)}`;
    const faux = [];
    const indices = shuffle(termes.flatMap((_, i) => termes.map((__, j) => [i, j]).filter(([a, b]) => a < b)));
    // Les paires distractrices sont elles aussi de SIGNES CONTRAIRES : une paire
    // de même signe ne peut jamais être opposée, l'élève l'écarterait sans
    // comparer, et la proposition serait fausse à chaque tirage.
    for (const [i, j] of indices) {
        if (faux.length >= 3) break;
        if (termes[i] + termes[j] !== 0 && (termes[i] > 0) !== (termes[j] > 0)) {
            const paire = `${cent(termes[i])} et ${cent(termes[j])}`;
            if (!faux.includes(paire)) faux.push(paire);
        }
    }
    const forme = randomChoice(FORMES);
    const payload = makeQcm([correct, ...faux], correct,
        `<p>On veut calculer <strong>${ecrireSomme(termes, forme)}</strong> le plus simplement possible. `
        + '<b>Quels sont les deux termes opposés, dont la somme vaut 0, qu\'on a intérêt à regrouper d\'abord ?</b></p>',
        opts(L_ASTUCE));
    payload._meta.params = {
        x, termes, forme, niveau: 3,
        distracteurs: faux.map(v => ({ valeur: v, code: 'terme_compte_deux_fois' })),
    };
    return payload;
};

const genAstuceSaisie = () => {
    const { x, termes, reste } = tirerAstuce();
    const forme = randomChoice(FORMES);
    const phrase = `Dans ${ecrireSomme(termes, forme)}, le terme opposé à ${cent(x)} est {{${cent(-x)}}}.\n`
        + `En regroupant ces deux termes, dont la somme vaut 0, la somme totale vaut {{${cent(reste)}}}.`;
    const payload = makeTexteTrou(phrase,
        `<p><b>Repère les opposés et calcule astucieusement.</b> ${CONSIGNE_RES}</p>`,
        { ...opts(L_ASTUCE), mode: 'saisie' });
    payload._meta.params = { x, termes, forme, reste, niveau: 3, erreurVisee: 'terme_compte_deux_fois' };
    return payload;
};

// ═════════ Carte « situation » — Le bilan d'une situation (niveau 6) ═════════
// Le nombre d'événements du SECOND type n'est jamais donné : il se déduit du
// total. Les quatre contextes viennent de la liste fermée de la spécification.

const PRENOMS = ['Léa', 'Noé', 'Yanis', 'Inès', 'Maël', 'Sofia', 'Elias', 'Jade'];

const CONTEXTES = [
    {
        id: 'gains_pertes',
        unite: 'points',
        enonce: (p, s) => `${p} joue ${s.n} manches et en gagne ${s.k}. `
            + `Chaque manche gagnée rapporte ${cent(s.a)} point${s.a >= 200 ? 's' : ''}, chaque manche perdue en fait perdre ${cent(s.b)} point${s.b >= 200 ? 's' : ''}.`,
        question: 'Quel est le bilan de points à la fin de la partie ?',
    },
    {
        id: 'temperatures',
        unite: '°C',
        enonce: (p, s) => `Pendant ${s.n} heures, ${p} relève la température. Elle monte pendant ${s.k} heure${s.k > 1 ? 's' : ''}, `
            + `de ${cent(s.a)} °C par heure, et chacune des autres heures elle baisse de ${cent(s.b)} °C.`,
        question: 'De combien la température a-t-elle varié en tout ?',
    },
    {
        id: 'altitudes',
        unite: 'm',
        enonce: (p, s) => `${p} marche en montagne et fait ${s.n} étapes. ${s.k} étape${s.k > 1 ? 's montent' : ' monte'} `
            + `de ${cent(s.a)} m, chacune des autres étapes descend de ${cent(s.b)} m.`,
        question: 'De combien son altitude a-t-elle varié en tout ?',
    },
    {
        id: 'compte',
        unite: '€',
        enonce: (p, s) => `Le compte de ${p} enregistre ${s.n} mouvements. ${s.k} ${s.k > 1 ? 'sont des dépôts' : 'est un dépôt'} `
            + `de ${cent(s.a)} €, chacun des autres mouvements est un retrait de ${cent(s.b)} €.`,
        question: 'De combien le compte a-t-il varié en tout ?',
    },
];

/**
 * Une situation : n événements (3 à 6), k du premier type (donné), n-k du
 * second (à DÉDUIRE), montant a > 0 et montant b > 0 jamais égaux, bilan non nul.
 */
const tirerSituation = () => {
    for (let essai = 0; essai < 80; essai++) {
        const n = randInt(3, 6);
        const k = randInt(1, n - 1);
        const a = tirerTerme(randomChoice([0, 1, 2]));
        const b = tirerTerme(randomChoice([0, 1, 2]));
        const bilan = k * a - (n - k) * b;
        if (a === b || bilan === 0 || a < 50 || b < 50) continue; // montants ≥ 0,5
        if (Math.abs(bilan) > 12000) continue;                    // bilan lisible
        return { n, k, a, b, bilan };
    }
    return { n: 4, k: 3, a: 250, b: 175, bilan: 3 * 250 - 175 }; // repli déterministe
};

const genSituationQcm = () => {
    const ctx = randomChoice(CONTEXTES);
    const prenom = randomChoice(PRENOMS);
    const mot = (v, u) => `${v > 0 ? 'Positif' : 'Négatif'}, de ${cent(Math.abs(v))} ${u}`;
    // Distracteurs : le signe inversé ; l'effectif du second type non déduit
    // (une seule fois au lieu de n - k) ; les effectifs comptés comme des
    // montants ; les deux groupes ajoutés au lieu d'être retranchés.
    const construire = () => {
        const sit = tirerSituation();
        const candidats = [
            { v: -sit.bilan, code: 'signe_du_plus_grand_terme' },
            { v: sit.k * sit.a - sit.b, code: 'effectif_non_deduit' },
            { v: (sit.k * 100 + sit.a) - ((sit.n - sit.k) * 100 + sit.b), code: 'effectifs_confondus_avec_montants' },
            { v: sit.k * sit.a + (sit.n - sit.k) * sit.b, code: 'effectif_non_deduit' },
            { v: sit.a - (sit.n - sit.k) * sit.b, code: 'effectifs_confondus_avec_montants' },
        ];
        const vus = new Set([mot(sit.bilan, ctx.unite)]);
        const distracteurs = [];
        candidats.forEach((c) => {
            if (distracteurs.length < 3 && c.v !== 0 && !vus.has(mot(c.v, ctx.unite))) {
                vus.add(mot(c.v, ctx.unite));
                distracteurs.push({ valeur: mot(c.v, ctx.unite), code: c.code });
            }
        });
        return { sit, distracteurs };
    };
    let t = construire();
    for (let essai = 0; essai < 60 && t.distracteurs.length < 3; essai++) t = construire();
    if (t.distracteurs.length < 3) { // repli déterministe, vérifié à la main
        const sit = { n: 4, k: 3, a: 250, b: 175, bilan: 3 * 250 - 175 };
        t = {
            sit,
            distracteurs: [
                { valeur: mot(-sit.bilan, ctx.unite), code: 'signe_du_plus_grand_terme' },
                { valeur: mot(sit.k * sit.a - sit.b, ctx.unite), code: 'effectif_non_deduit' },
                { valeur: mot(sit.k * 100 + sit.a - ((sit.n - sit.k) * 100 + sit.b), ctx.unite), code: 'effectifs_confondus_avec_montants' },
            ],
        };
    }
    const payload = makeQcm([mot(t.sit.bilan, ctx.unite), ...t.distracteurs.map(d => d.valeur)],
        mot(t.sit.bilan, ctx.unite),
        `<p>${ctx.enonce(prenom, t.sit)} <b>${ctx.question}</b> Le bilan est-il positif ou négatif, et de combien ?</p>`,
        opts(L_SITUATION));
    payload._meta.params = { ...t.sit, contexte: ctx.id, prenom, niveau: 6, distracteurs: t.distracteurs };
    return payload;
};

const genSituationSaisie = () => {
    const s = tirerSituation();
    const ctx = randomChoice(CONTEXTES);
    const prenom = randomChoice(PRENOMS);
    const payload = makeSaisie(cent(s.bilan),
        `<p>${ctx.enonce(prenom, s)} <b>${ctx.question}</b> `
        + `Écris le bilan en écriture décimale, avec son signe s'il est négatif.</p>`,
        { ...opts(L_SITUATION), suffixe: ctx.unite });
    payload._meta.params = { ...s, contexte: ctx.id, prenom, niveau: 6, erreurVisee: 'effectif_non_deduit' };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    groupes_texte_trou_saisie: genGroupesSaisie,
    groupes_qcm_unique: genGroupesQcm,
    groupes_vf: genGroupesVf,
    astuce_opposes_qcm_unique: genAstuceQcm,
    astuce_texte_trou_saisie: genAstuceSaisie,
    situation_qcm_unique: genSituationQcm,
    situation_texte: genSituationSaisie,
};

const FORMAT_LABELS = {
    texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM', vf: 'Vrai/Faux',
    opposes_qcm_unique: 'QCM Opposés', texte: 'Saisie',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'groupes', label: L_GROUPES, formatsList: buildFormats('groupes') },
    { id: 'astuce', label: L_ASTUCE, formatsList: buildFormats('astuce') },
    { id: 'situation', label: L_SITUATION, formatsList: buildFormats('situation') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
